import type {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import type { StoreApi } from 'zustand';

import { apiSso } from '~/configs/api/apiConfig';
import env from '~/configs/env';
import type { IUserRoleEnum } from '~/enums/userRole.enum';
import { singleFlightRefresh } from '~/services/auth/refresh.lock';
import { logoutSession } from '~/services/sessions/SessionsService';
import { emitTokenChanged } from '~/services/token/token.events';
import { clearExpiryCache, getSecondsLeftCached } from '~/stores/token/useTokenExpiryStore';
import { useTokenStore } from '~/stores/token/useTokenStore';
import { toClaimArray } from '~/utils/auth/normalizeClaim';
import type { IAuthStore, ITokenClaim, IVeilingTokemClaim } from './useAuthStore';
import { defaultTokenClaim } from './useAuthStore';

export type IRefreshData = {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
  scope?: string;
};

type IHandleAxiosErrorOptions = {
  tokenEndpoint?: string;
};

type IEnsureValidTokenOptions = {
  tokenEndpoint?: string;
};

type IRetryableRequestConfig = InternalAxiosRequestConfig & { _retry?: boolean };

export type IAuthTokenSlice = {
  // sync claim
  syncTokenClaimFromAccess: () => void;

  // auth actions
  refreshToken: () => Promise<string>;
  forceRefresh: () => Promise<string>;
  ensureValidToken: (requestUrl?: string, options?: IEnsureValidTokenOptions) => Promise<void>;

  // helpers
  getTokenToHeader: () => string;

  // logout
  logoutLocal: () => void;
  logout: () => Promise<void>;
  redirectSingleSignOn: () => void;

  // axios
  handleAxiosError: (
    error: unknown,
    client?: AxiosInstance,
    options?: IHandleAxiosErrorOptions,
  ) => Promise<AxiosResponse<unknown>>;
};

// ==================== helpers ====================

const redirectToSSOLogoff = (): void => {
  const ssoBase = env.SINGLE_SIGNON?.trim();

  if (!ssoBase) {
    console.error('BASE_URL não definida!');
    return;
  }
  window.location.href = new URL('logoff', ssoBase).toString();
};

const getConfig = (error: AxiosError<unknown>): IRetryableRequestConfig | undefined =>
  error.config as IRetryableRequestConfig | undefined;

const getUrl = (config?: AxiosRequestConfig): string => String(config?.url ?? '');

const getStatus = (error: AxiosError<unknown>): number => error.response?.status ?? 0;

const isTokenEndpointCall = (url: string, tokenEndpoint: string): boolean =>
  url.includes(tokenEndpoint);

const resolveTokenEndpoint = (v?: string): string => v ?? '/connect/token';

const hasAccessToken = (): boolean => Boolean(useTokenStore.getAccess());

const hasRefreshToken = (): boolean => Boolean(useTokenStore.getRefresh());

const validateAccessOrLogout = (logoutFn: () => void): boolean => {
  if (hasAccessToken()) return true;
  logoutFn();
  return false;
};

const setAuthHeader = (config: IRetryableRequestConfig, accessToken: string) => {
  const tokenType = useTokenStore.getTokenType() || 'Bearer';
  config.headers = config.headers ?? {};
  config.headers.Authorization = `${tokenType} ${accessToken}`;
};

const decodeClaim = (accessToken: string): ITokenClaim => {
  try {
    const decoded = jwtDecode(accessToken) as Partial<IVeilingTokemClaim> | null;
    if (!decoded) return defaultTokenClaim;

    return {
      roleId: toClaimArray(decoded.veiling_claim_type_role).map(Number) as IUserRoleEnum[],
      identifier: Number(decoded.veiling_claim_type_identifier ?? 0),
      email: decoded.veiling_claim_type_email ?? '',
      name: decoded.veiling_claim_type_name ?? '',
      allowedSystems: toClaimArray(decoded.veiling_claim_type_allowed_systems),
    } satisfies ITokenClaim;
  } catch {
    return defaultTokenClaim;
  }
};

// ==================== slice factory ====================

type ISetState = StoreApi<IAuthStore>['setState'];
type IGetState = StoreApi<IAuthStore>['getState'];

export default function useAuthTokenSlice(_set: ISetState, get: IGetState): IAuthTokenSlice {
  const syncTokenClaimFromAccess = () => {
    const access = useTokenStore.getAccess();
    const claim = access ? decodeClaim(access) : defaultTokenClaim;

    get().setData(state => {
      state.tokenClaim = claim;
    });
  };

  const logoutLocal = () => {
    useTokenStore.clearAll();
    clearExpiryCache();
    emitTokenChanged();

    get().setData(state => {
      state.tokenClaim = defaultTokenClaim;
    });
  };

  const logout = async () => {
    const refresh = useTokenStore.getRefresh();
    if (refresh) {
      try {
        await logoutSession(refresh);
      } catch {
        // best-effort: continua mesmo se backend falhar
      }
    }
    logoutLocal();
    redirectToSSOLogoff();
  };

  const refreshToken = async (): Promise<string> => {
    const refreshTokenValue = useTokenStore.getRefresh();
    const scope = useTokenStore.getScope();

    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      refresh_token: refreshTokenValue,
      client_id: 'veiling-online',
      client_secret: env.CLIENT_SECRET,
      scope,
    });

    const { data } = await apiSso.post<IRefreshData>('/connect/token', body.toString(), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });

    useTokenStore.setAll({
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
      expiresIn: String(data.expires_in),
      scope: data.scope ?? scope,
      tokenType: data.token_type,
    });

    clearExpiryCache();
    emitTokenChanged();

    // mantém o claim em dia
    syncTokenClaimFromAccess();

    return data.access_token;
  };

  const forceRefresh = async (): Promise<string> => {
    if (!hasRefreshToken()) {
      await logout();
      throw new Error('Missing refresh token');
    }

    return singleFlightRefresh(() => refreshToken());
  };

  const ensureValidToken = async (
    requestUrl?: string,
    options: IEnsureValidTokenOptions = {},
  ): Promise<void> => {
    const tokenEndpoint = resolveTokenEndpoint(options.tokenEndpoint);

    const url = requestUrl ?? '';
    if (isTokenEndpointCall(url, tokenEndpoint)) return;

    if (!validateAccessOrLogout(logout)) return;

    const secondsLeft = getSecondsLeftCached();
    if (secondsLeft <= 0) return;
  };

  const getTokenToHeader = (): string => {
    const access = useTokenStore.getAccess();
    const tokenType = useTokenStore.getTokenType() || 'Bearer';
    return access ? `${tokenType} ${access}` : '';
  };

  const handleAxiosError = async (
    error: unknown,
    client: AxiosInstance = axios,
    options: IHandleAxiosErrorOptions = {},
  ): Promise<AxiosResponse<unknown>> => {
    const tokenEndpoint = resolveTokenEndpoint(options.tokenEndpoint);

    if (!axios.isAxiosError(error)) throw error;

    const axiosError = error as AxiosError<unknown>;
    const originalConfig = getConfig(axiosError);
    if (!originalConfig) throw axiosError;

    const url = getUrl(originalConfig);
    const status = getStatus(axiosError);

    if (isTokenEndpointCall(url, tokenEndpoint)) {
      await logout();
      throw axiosError;
    }

    if (status !== 401) throw axiosError;

    if (!hasRefreshToken()) {
      await logout();
      throw axiosError;
    }

    if (originalConfig._retry) {
      await logout();
      throw axiosError;
    }
    originalConfig._retry = true;

    try {
      const newAccess = await forceRefresh();
      setAuthHeader(originalConfig, newAccess);
      return await client.request(originalConfig);
    } catch (e) {
      await logout();
      throw e;
    }
  };

  return {
    syncTokenClaimFromAccess,
    refreshToken,
    forceRefresh,
    ensureValidToken,
    getTokenToHeader,
    logoutLocal,
    logout,
    redirectSingleSignOn: redirectToSSOLogoff,
    handleAxiosError,
  };
}
